/**
 * ShopPage.jsx — Ceramic Pieces Browse & Purchase (Square Catalog API + Web Payments SDK)
 *
 * Views: browse (single-item carousel) → checkout → success
 *
 * Data source: getShopProducts Firebase function (Square Catalog + Inventory).
 * Falls back to local SHOP_ITEMS when the function is unavailable (local dev)
 * so the UI always renders something.
 *
 * Two levels of navigation:
 *   Piece carousel — prev/next arrows + dots at the bottom navigate between items.
 *   Photo carousel — prev/next arrows + dots inside the photo tile navigate between
 *                    photos of the current item (Square supports multiple images).
 *                    Photo index resets to 0 whenever the active piece changes.
 */
import { useState, useEffect, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { SHOP_ITEMS } from '../data/shopItems';
import { useShopContent } from '../services/firestore';
import { functions } from '../firebase';

const APP_ID = import.meta.env.VITE_SQUARE_APP_ID;
const LOCATION_ID = import.meta.env.VITE_SQUARE_LOCATION_ID;

/* ── Dev fallback — maps static shopItems shape to API shape ─────────────── */
const FALLBACK_PRODUCTS = SHOP_ITEMS.map((item) => ({
  id: String(item.id),
  variationId: `local-var-${item.id}`,
  name: item.title,
  tag: item.tag,
  description: item.description,
  price: item.price,
  stock: 3,
  photos: item.photos,
}));

/* ── Shared style tokens ─────────────────────────────────────────────────── */
const tile = {
  border: '1px solid #E8A020',
  background: '#f9f2e9',
  backgroundImage: 'none',
  borderRadius: 16,
  overflow: 'hidden',
};
const cap = {
  fontFamily: "'Raleway', sans-serif",
  fontWeight: 700,
  fontSize: '0.7rem',
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
};

/* ── Sub-components ──────────────────────────────────────────────────────── */
function StockBadge({ stock }) {
  if (stock === 0)
    return <span className="shop-stock-badge sold-out">Sold Out</span>;
  if (stock <= 2)
    return (
      <span className="shop-stock-badge low-stock">Only {stock} left</span>
    );
  return <span className="shop-stock-badge in-stock">{stock} in stock</span>;
}

function PhotoPlaceholder() {
  const corners = [
    { top: 14, left: 18 },
    { top: 14, right: 18 },
    { bottom: 14, left: 18 },
    { bottom: 14, right: 18 },
  ];
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {corners.map((pos, k) => (
        <svg
          key={k}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          style={{ position: 'absolute', opacity: 0.2, ...pos }}
        >
          <line x1="0" y1="0" x2="12" y2="0" stroke="#9C7A3C" strokeWidth="1" />
          <line x1="0" y1="0" x2="0" y2="12" stroke="#9C7A3C" strokeWidth="1" />
        </svg>
      ))}
      <span
        style={{
          ...cap,
          color: '#ccc',
          fontSize: '0.65rem',
          textTransform: 'none',
          letterSpacing: '0.1em',
        }}
      >
        Photo Coming Soon
      </span>
    </div>
  );
}

/* ── Main component ──────────────────────────────────────────────────────── */
export default function ShopPage({ onReset }) {
  const { shop } = useShopContent();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0); // active piece index
  const [photoIdx, setPhotoIdx] = useState(0); // active photo within current piece
  const [view, setView] = useState('browse'); // 'browse' | 'checkout' | 'success'
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false); // highlight missing email on Pay click
  const [usedEmail, setUsedEmail] = useState(''); // email actually used for the completed order
  const [processing, setProcessing] = useState(false);
  const [payError, setPayError] = useState(null);
  const [orderResult, setOrderResult] = useState(null);
  const [qty, setQty] = useState(1);
  const [fulfillment, setFulfillment] = useState('pickup'); // 'pickup' | 'ship'
  const [shipAddr, setShipAddr] = useState({
    line1: '',
    line2: '',
    city: '',
    state: '',
    zip: '',
  });
  const [applePayReady, setApplePayReady] = useState(false);
  const [googlePayReady, setGooglePayReady] = useState(false);
  const cardRef = useRef(null);
  const appleRef = useRef(null);
  const googlePayRef = useRef(null);

  /* Scroll to top on every view change */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [view]);

  /* Reset photo index and quantity whenever the active piece changes */
  useEffect(() => {
    setPhotoIdx(0);
    setQty(1);
  }, [idx]);

  /* Fetch from Square via Firebase function; fall back to local data if unavailable */
  function loadProducts() {
    setLoading(true);
    const getShopProducts = httpsCallable(functions, 'getShopProducts');
    getShopProducts()
      .then(({ data }) => {
        const list = data?.products;
        setProducts(
          Array.isArray(list) && list.length > 0 ? list : FALLBACK_PRODUCTS,
        );
        setLoading(false);
      })
      .catch(() => {
        setProducts(FALLBACK_PRODUCTS);
        setLoading(false);
      });
  }
  useEffect(() => {
    loadProducts();
  }, []);

  /* Init Square Web Payments SDK when checkout view opens */
  useEffect(() => {
    if (view !== 'checkout' || !window.Square || !APP_ID || !LOCATION_ID)
      return;
    let alive = true;
    async function init() {
      try {
        const payments = window.Square.payments(APP_ID, LOCATION_ID);
        const card = await payments.card({
          style: {
            '.input-container': { borderColor: '#DDD8CC', borderRadius: '2px' },
            '.input-container.is-focus': { borderColor: '#9C7A3C' },
          },
        });
        await card.attach('#square-card-container');
        if (alive) cardRef.current = card;
        const item = products[idx];
        if (item) {
          // Shared payment request used by both Apple Pay and Google Pay
          const req = payments.paymentRequest({
            countryCode: 'US',
            currencyCode: 'USD',
            total: {
              amount: String((item.price * qty).toFixed(2)),
              label: qty > 1 ? `${item.name} ×${qty}` : item.name,
            },
            // requestShippingContact returns email, name, phone + shipping address
            // at result.details.shipping.contact (documented Square SDK field)
            requestShippingContact: true,
          });

          // ── Apple Pay ───────────────────────────────────────────────────
          try {
            // applePay() returns an instance to call .tokenize() on — no .attach()
            const apple = await payments.applePay(req);
            if (alive) {
              appleRef.current = apple;
              setApplePayReady(true);
            }
          } catch (appleErr) {
            console.warn(
              'Apple Pay unavailable:',
              appleErr?.message ?? appleErr,
            );
          }

          // ── Google Pay (Chrome/Android only — skip on Safari) ───────────
          const isSafari = /^((?!chrome|android).)*safari/i.test(
            navigator.userAgent,
          );
          if (!isSafari)
            try {
              const goog = await payments.googlePay(req);
              await goog.attach('#google-pay-button', {
                buttonColor: 'black',
                buttonType: 'buy',
                buttonSizeMode: 'fill',
              });
              goog.addEventListener('ontokenization', (event) => {
                const { tokenResult } = event.detail;
                if (tokenResult.status === 'OK') {
                  const sc = tokenResult.details?.shipping?.contact ?? {};
                  const bc = tokenResult.details?.billing ?? {};
                  const gpEmail = sc.email || bc.email || email;
                  if (gpEmail) setEmail(gpEmail);
                  submitPayment(
                    tokenResult.token,
                    gpEmail || email,
                    {
                      givenName: sc.givenName || bc.givenName,
                      familyName: sc.familyName || bc.familyName,
                      email: gpEmail || email,
                      phone: sc.phone || bc.phone,
                      addressLine1:
                        sc.addressLines?.[0] || bc.addressLines?.[0],
                      addressLine2:
                        sc.addressLines?.[1] || bc.addressLines?.[1],
                      city: sc.city || bc.city,
                      state: sc.state || bc.state,
                      postalCode: sc.postalCode || bc.postalCode,
                      countryCode: sc.countryCode || bc.countryCode,
                    },
                    fulfillment,
                  );
                } else {
                  setPayError(
                    tokenResult.errors?.map((e) => e.message).join(' ') ||
                      'Google Pay failed.',
                  );
                  setProcessing(false);
                }
              });
              if (alive) {
                googlePayRef.current = goog;
                setGooglePayReady(true);
              }
            } catch (googErr) {
              console.warn(
                'Google Pay unavailable:',
                googErr?.message ?? googErr,
              );
            }
        }
      } catch (err) {
        console.error('Square SDK init:', err);
      }
    }
    init();
    return () => {
      alive = false;
      cardRef.current?.destroy?.();
      cardRef.current = null;
      appleRef.current = null;
      googlePayRef.current?.destroy?.();
      googlePayRef.current = null;
      setApplePayReady(false);
      setGooglePayReady(false);
    };
    // email/submitPayment omitted intentionally — adding them would reinitialize
    // the Square SDK on every keystroke. Google Pay billing contact provides email.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, idx, products, qty]);

  const item = products[idx] ?? null;

  async function handleApplePay() {
    if (!appleRef.current) return;
    setProcessing(true);
    setPayError(null);
    try {
      const result = await appleRef.current.tokenize();
      if (result.status === 'OK') {
        // email in result.details.shipping.contact (requestShippingContact)
        // result.details.billing is identical fallback (confirmed via console.log)
        const sc = result.details?.shipping?.contact ?? {};
        const bc = result.details?.billing ?? {};
        const appleEmail = sc.email || bc.email || email;
        if (appleEmail) setEmail(appleEmail);
        await submitPayment(
          result.token,
          appleEmail || email,
          {
            givenName: sc.givenName || bc.givenName,
            familyName: sc.familyName || bc.familyName,
            email: appleEmail || email,
            phone: sc.phone || bc.phone,
            addressLine1: sc.addressLines?.[0] || bc.addressLines?.[0],
            addressLine2: sc.addressLines?.[1] || bc.addressLines?.[1],
            city: sc.city || bc.city,
            state: sc.state || bc.state,
            postalCode: sc.postalCode || bc.postalCode,
            countryCode: sc.countryCode || bc.countryCode,
          },
          fulfillment,
        );
      } else {
        setPayError(
          result.errors?.map((e) => e.message).join(' ') || 'Apple Pay failed.',
        );
      }
    } catch {
      setPayError('Apple Pay was cancelled.');
    } finally {
      setProcessing(false);
    }
  }

  async function handleCardPay() {
    if (!cardRef.current) return;
    if (!email) {
      setEmailError(true);
      return;
    }
    if (
      fulfillment === 'ship' &&
      (!shipAddr.line1 || !shipAddr.city || !shipAddr.state || !shipAddr.zip)
    ) {
      setPayError('Please complete your shipping address.');
      return;
    }
    setProcessing(true);
    setPayError(null);
    try {
      const result = await cardRef.current.tokenize();
      if (result.status === 'OK') {
        const bc = {
          email,
          ...(fulfillment === 'ship' && {
            addressLine1: shipAddr.line1,
            addressLine2: shipAddr.line2 || undefined,
            city: shipAddr.city,
            state: shipAddr.state,
            postalCode: shipAddr.zip,
            countryCode: 'US',
          }),
        };
        await submitPayment(result.token, email, bc, fulfillment);
      } else {
        setPayError(
          result.errors?.map((e) => e.message).join(' ') || 'Card error.',
        );
      }
    } catch {
      setPayError('Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  }

  async function submitPayment(
    sourceId,
    emailOverride,
    billingContact = null,
    fulfillmentType = fulfillment,
  ) {
    setProcessing(true);
    setPayError(null);
    try {
      const processShopOrder = httpsCallable(functions, 'processShopOrder');
      const { data } = await processShopOrder({
        sourceId,
        variationId: item.variationId,
        name: item.name,
        price: item.price,
        quantity: qty,
        email: emailOverride || email,
        billingContact,
        fulfillment: fulfillmentType,
      });
      if (data.success) {
        setUsedEmail(emailOverride || email);
        setOrderResult(data);
        setView('success');
      } else {
        setPayError(data.error || 'Payment failed. Please try again.');
      }
    } catch (err) {
      console.error('Shop payment error:', err);
      setPayError('Payment failed. Please try again or contact us for help.');
    } finally {
      setProcessing(false);
    }
  }

  /* ─────────────────────────────────────────────────────────────────────────
     BROWSE VIEW — single item at a time, prev/next navigation
  ───────────────────────────────────────────────────────────────────────── */
  if (view === 'browse')
    return (
      <div
        className="fade section-pad"
        style={{ paddingTop: 28, paddingBottom: 48 }}
      >
        {/* ── Header tile ─────────────────────────────────────────────────── */}
        <div
          style={{
            ...tile,
            padding: '32px 48px 28px',
            position: 'relative',
            border: '4px solid #E8A020',
            marginBottom: 40,
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 14,
              left: 20,
              color: '#ccc',
              fontSize: '0.65rem',
              letterSpacing: 4,
            }}
          >
            ◦ ◦
          </div>
          <div
            style={{
              position: 'absolute',
              top: 14,
              right: 20,
              color: '#ccc',
              fontSize: '0.65rem',
              letterSpacing: 4,
            }}
          >
            ◦ ◦
          </div>
          <button
            onClick={onReset}
            style={{
              fontFamily: "'Raleway', sans-serif",
              fontWeight: 700,
              fontSize: '0.75rem',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#9C7A3C',
              marginBottom: 16,
              display: 'block',
            }}
          >
            ← Back
          </button>
          <div style={{ ...cap, color: '#9C7A3C', marginBottom: 10 }}>
            ◦ {shop.page.label} ◦
          </div>
          <h2
            style={{
              fontFamily: "'Titan One', sans-serif",
              fontWeight: 400,
              fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
              lineHeight: 0.95,
              letterSpacing: '-0.01em',
              color: '#1a1208',
              marginBottom: 14,
            }}
          >
            {shop.page.heading}
          </h2>
          <p
            style={{
              fontFamily: "'Raleway', sans-serif",
              fontWeight: 700,
              fontSize: 'clamp(1rem, 1.8vw, 1.1rem)',
              color: '#1a1208',
              lineHeight: 1.65,
              maxWidth: 520,
            }}
          >
            {shop.page.description}
          </p>
        </div>

        {/* ── Loading ─────────────────────────────────────────────────────── */}
        {loading && (
          <div
            style={{
              ...tile,
              borderTop: 'none',
              padding: '60px',
              textAlign: 'center',
            }}
          >
            <p style={{ ...cap, color: '#bbb' }}>Loading the collection…</p>
          </div>
        )}

        {/* ── Empty ───────────────────────────────────────────────────────── */}
        {!loading && products.length === 0 && (
          <div
            style={{
              ...tile,
              borderTop: 'none',
              padding: '60px',
              textAlign: 'center',
            }}
          >
            <p style={{ color: '#888', fontStyle: 'italic' }}>
              New pieces coming soon — sign up for the newsletter to be
              notified.
            </p>
          </div>
        )}

        {/* ── Photo + nav tile ────────────────────────────────────────────── */}
        {!loading && item && (
          <div
            style={{
              ...tile,
              position: 'relative',
              overflow: 'hidden',
              marginBottom: 40,
            }}
          >
            {/* Photo — inner carousel when item has multiple images */}
            {(() => {
              const photos = item.photos ?? [];
              const photo = photos[photoIdx] ?? { url: '', caption: '' };
              return (
                <div
                  style={{
                    position: 'relative',
                    aspectRatio: '4/3',
                    background: '#EDE8DA',
                    overflow: 'hidden',
                  }}
                >
                  {photo.url ? (
                    <img
                      key={photo.url + photoIdx}
                      src={photo.url}
                      alt={photo.caption || item.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  ) : (
                    <PhotoPlaceholder />
                  )}

                  {/* Photo prev/next — only shown when item has more than one photo */}
                  {photos.length > 1 && (
                    <>
                      <button
                        className="nav-circle"
                        onClick={() =>
                          setPhotoIdx(
                            (p) => (p - 1 + photos.length) % photos.length,
                          )
                        }
                        aria-label="Previous photo"
                        style={{
                          position: 'absolute',
                          left: 16,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          zIndex: 2,
                          background: 'rgba(249,242,233,0.85)',
                        }}
                      >
                        ‹
                      </button>
                      <button
                        className="nav-circle"
                        onClick={() =>
                          setPhotoIdx((p) => (p + 1) % photos.length)
                        }
                        aria-label="Next photo"
                        style={{
                          position: 'absolute',
                          right: 16,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          zIndex: 2,
                          background: 'rgba(249,242,233,0.85)',
                        }}
                      >
                        ›
                      </button>

                      {/* Photo dots — bottom-center overlay */}
                      <div
                        style={{
                          position: 'absolute',
                          bottom: 14,
                          left: 0,
                          right: 0,
                          display: 'flex',
                          justifyContent: 'center',
                          gap: 7,
                          zIndex: 2,
                        }}
                      >
                        {photos.map((_, p) => (
                          <button
                            key={p}
                            className={
                              p === photoIdx
                                ? 'carousel-dot-photo active'
                                : 'carousel-dot-photo'
                            }
                            onClick={() => setPhotoIdx(p)}
                            aria-label={`Photo ${p + 1}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              );
            })()}

            {/* Piece navigation bar */}
            <div
              style={{
                padding: '10px 32px 16px',
                borderTop: '1px solid #ddd8cc',
              }}
            >
              {/* Label so visitors know this scrolls between pieces */}
              <p
                style={{
                  fontFamily: "'Raleway', sans-serif",
                  fontWeight: 700,
                  color: '#9C7A3C',
                  fontSize: '0.7rem',
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  textAlign: 'center',
                  margin: '0 0 10px',
                }}
              >
                ← Browse the Collection →
              </p>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <button
                  className="nav-circle"
                  onClick={() =>
                    setIdx((i) => (i - 1 + products.length) % products.length)
                  }
                  aria-label="Previous piece"
                >
                  ‹
                </button>

                {/* Dot per piece */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {products.map((_, i) => (
                    <button
                      key={i}
                      className={
                        i === idx ? 'carousel-dot active' : 'carousel-dot'
                      }
                      onClick={() => setIdx(i)}
                      aria-label={`Piece ${i + 1}`}
                    />
                  ))}
                </div>

                <button
                  className="nav-circle"
                  onClick={() => setIdx((i) => (i + 1) % products.length)}
                  aria-label="Next piece"
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Details tile ────────────────────────────────────────────────── */}
        {!loading && item && (
          <div style={{ ...tile, padding: '36px 48px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: 20,
                gap: 16,
              }}
            >
              <div>
                <div
                  style={{
                    ...cap,
                    color: '#9C7A3C',
                    fontSize: '0.62rem',
                    marginBottom: 6,
                  }}
                >
                  {item.tag}
                </div>
                <h3
                  style={{
                    fontFamily: "'Titan One', sans-serif",
                    fontWeight: 400,
                    fontSize: '1.6rem',
                    letterSpacing: '-0.01em',
                    color: '#1a1208',
                    marginBottom: 8,
                  }}
                >
                  {item.name}
                </h3>
                <div
                  style={{
                    fontFamily: "'Titan One', sans-serif",
                    fontWeight: 400,
                    fontSize: '1.5rem',
                    color: '#1a1208',
                  }}
                >
                  ${item.price.toFixed(2)}
                </div>
              </div>
              <StockBadge stock={item.stock} />
            </div>

            <p
              style={{
                fontFamily: "'Raleway', sans-serif",
                fontWeight: 700,
                fontSize: 'clamp(1rem, 1.8vw, 1.1rem)',
                color: '#1a1208',
                lineHeight: 1.65,
                marginBottom: 20,
              }}
            >
              {item.description}
            </p>
            <p
              style={{
                fontFamily: "'Raleway', sans-serif",
                fontWeight: 700,
                color: '#9C7A3C',
                fontSize: '0.75rem',
                letterSpacing: '0.06em',
                marginBottom: 20,
              }}
            >
              Ships from Dallas, Texas &nbsp;·&nbsp; Carefully packaged
            </p>

            {item.stock > 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 20,
                  marginBottom: 24,
                }}
              >
                <span style={{ ...cap, color: '#9C7A3C', fontSize: '0.63rem' }}>
                  Quantity
                </span>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    border: '1px solid #ddd8cc',
                  }}
                >
                  <button
                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                    disabled={qty <= 1}
                    aria-label="Decrease quantity"
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: qty <= 1 ? 'default' : 'pointer',
                      padding: '6px 14px',
                      fontSize: '1.1rem',
                      color: qty <= 1 ? '#ddd' : '#1a1208',
                      fontFamily: "'Raleway', sans-serif",
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                  >
                    −
                  </button>
                  <span
                    style={{
                      padding: '6px 4px',
                      minWidth: 28,
                      textAlign: 'center',
                      fontFamily: "'Raleway', sans-serif",
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      borderLeft: '1px solid #ddd8cc',
                      borderRight: '1px solid #ddd8cc',
                    }}
                  >
                    {qty}
                  </span>
                  <button
                    onClick={() => setQty((q) => Math.min(item.stock, q + 1))}
                    disabled={qty >= item.stock}
                    aria-label="Increase quantity"
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: qty >= item.stock ? 'default' : 'pointer',
                      padding: '6px 14px',
                      fontSize: '1.1rem',
                      color: qty >= item.stock ? '#ddd' : '#1a1208',
                      fontFamily: "'Raleway', sans-serif",
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                  >
                    +
                  </button>
                </div>
                {qty > 1 && (
                  <span
                    style={{
                      fontFamily: "'Raleway', sans-serif",
                      fontWeight: 700,
                      color: '#9C7A3C',
                      fontSize: '0.85rem',
                    }}
                  >
                    ${(item.price * qty).toFixed(2)} total
                  </span>
                )}
              </div>
            )}

            {item.stock > 0 ? (
              <button
                className="book-btn"
                onClick={() => {
                  setPayError(null);
                  setView('checkout');
                }}
              >
                Buy Now — ${(item.price * qty).toFixed(2)}
                {qty > 1 && ` (${qty} pieces)`}
              </button>
            ) : (
              <p
                style={{
                  fontFamily: "'Raleway', sans-serif",
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  letterSpacing: '0.1em',
                  color: '#9C7A3C',
                  paddingTop: 16,
                  borderTop: '1px solid #ddd8cc',
                }}
              >
                This piece has sold.
              </p>
            )}
          </div>
        )}
      </div>
    );

  /* ─────────────────────────────────────────────────────────────────────────
     CHECKOUT VIEW
  ───────────────────────────────────────────────────────────────────────── */
  if (view === 'checkout' && item) {
    const sdkReady = !!(APP_ID && LOCATION_ID && window.Square);
    return (
      <div
        className="fade section-pad"
        style={{ paddingTop: 28, paddingBottom: 48 }}
      >
        <button
          onClick={() => setView('browse')}
          style={{
            fontFamily: "'Raleway', sans-serif",
            fontWeight: 700,
            fontSize: '0.75rem',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#9C7A3C',
            marginBottom: 20,
          }}
        >
          ← Back to {item.name}
        </button>

        <div className="checkout-grid">
          {/* Order summary */}
          <div style={{ ...tile, padding: '36px 40px' }}>
            <div style={{ ...cap, color: '#9C7A3C', marginBottom: 20 }}>
              ◦ Your Order ◦
            </div>

            {/* Item row */}
            <div
              style={{
                display: 'flex',
                gap: 16,
                alignItems: 'center',
                marginBottom: 24,
                paddingBottom: 20,
                borderBottom: '1px solid #ddd8cc',
              }}
            >
              <div
                style={{
                  width: 72,
                  height: 72,
                  background: '#EDE8DA',
                  flexShrink: 0,
                  overflow: 'hidden',
                  position: 'relative',
                  border: '1px solid #ddd8cc',
                }}
              >
                {item.photos?.[0]?.url ? (
                  <img
                    src={item.photos[0].url}
                    alt={item.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <PhotoPlaceholder />
                )}
              </div>
              <div>
                <div
                  style={{
                    ...cap,
                    color: '#9C7A3C',
                    fontSize: '0.6rem',
                    marginBottom: 4,
                  }}
                >
                  {item.tag}
                </div>
                <div
                  style={{
                    fontFamily: "'Titan One', sans-serif",
                    fontWeight: 400,
                    fontSize: '1rem',
                    letterSpacing: '-0.01em',
                    color: '#1a1208',
                    marginBottom: 4,
                  }}
                >
                  {item.name}
                </div>
                <div>
                  <div
                    style={{
                      fontFamily: "'Titan One', sans-serif",
                      fontWeight: 400,
                      fontSize: '1.1rem',
                      color: '#1a1208',
                    }}
                  >
                    ${item.price.toFixed(2)}
                  </div>
                  {qty > 1 && (
                    <div
                      style={{
                        ...cap,
                        color: '#9C7A3C',
                        fontSize: '0.6rem',
                        marginTop: 3,
                      }}
                    >
                      Qty: {qty}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {[
              ['Subtotal', `$${(item.price * qty).toFixed(2)}`],
              [
                'Fulfillment',
                fulfillment === 'pickup'
                  ? 'Local Pickup — Dallas, TX'
                  : 'Ship to me',
              ],
            ].map(([k, v]) => (
              <div
                key={k}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 10,
                }}
              >
                <span style={{ ...cap, color: '#9C7A3C', fontSize: '0.63rem' }}>
                  {k}
                </span>
                <span
                  style={{
                    fontFamily: "'Raleway', sans-serif",
                    fontWeight: 700,
                    fontSize: '0.88rem',
                  }}
                >
                  {v}
                </span>
              </div>
            ))}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                paddingTop: 14,
                borderTop: '1px solid #ddd8cc',
                marginTop: 6,
              }}
            >
              <span
                style={{
                  fontFamily: "'Raleway', sans-serif",
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: '#1a1208',
                }}
              >
                Total
              </span>
              <span
                style={{
                  fontFamily: "'Titan One', sans-serif",
                  fontWeight: 400,
                  fontSize: '1.1rem',
                  color: '#1a1208',
                }}
              >
                ${(item.price * qty).toFixed(2)}
              </span>
            </div>
            <p
              style={{
                marginTop: 24,
                fontSize: '0.78rem',
                color: '#bbb',
                fontStyle: 'italic',
                lineHeight: 1.7,
              }}
            >
              {fulfillment === 'pickup'
                ? "Local pickup in Dallas, TX — we'll coordinate by email. A receipt will be sent to your email."
                : 'Shipped carefully from Dallas, Texas. A receipt will be sent to your email.'}
            </p>
          </div>

          {/* Payment form */}
          <div
            className="checkout-payment"
            style={{ ...tile, padding: '36px 40px' }}
          >
            <div style={{ ...cap, color: '#9C7A3C', marginBottom: 20 }}>
              ◦ Payment ◦
            </div>

            {/* ── Fulfillment toggle ────────────────────────────────────── */}
            <div style={{ marginBottom: 24 }}>
              <div
                style={{
                  ...cap,
                  color: '#888',
                  fontSize: '0.63rem',
                  marginBottom: 8,
                }}
              >
                Delivery
              </div>
              <div style={{ display: 'flex', border: '1px solid #ddd8cc' }}>
                {[
                  { val: 'pickup', label: 'Local Pickup' },
                  { val: 'ship', label: 'Ship to Me — Coming Soon' },
                ].map(({ val, label }, i) => (
                  <button
                    key={val}
                    onClick={() => val !== 'ship' && setFulfillment(val)}
                    disabled={val === 'ship'}
                    style={{
                      flex: 1,
                      padding: '10px',
                      border: 'none',
                      borderRight: i === 0 ? '1px solid #ddd8cc' : 'none',
                      background:
                        fulfillment === val ? '#3D2010' : 'transparent',
                      color:
                        val === 'ship'
                          ? '#ccc'
                          : fulfillment === val
                            ? '#F9F2E9'
                            : '#888',
                      cursor: val === 'ship' ? 'not-allowed' : 'pointer',
                      ...cap,
                      fontSize: '0.65rem',
                      transition: 'background 0.15s, color 0.15s',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {fulfillment === 'pickup' && (
                <p
                  style={{
                    margin: '8px 0 0',
                    fontFamily: "'Raleway', sans-serif",
                    fontWeight: 700,
                    fontSize: '0.76rem',
                    color: '#9C7A3C',
                  }}
                >
                  Available in Dallas, TX — we&apos;ll coordinate pickup by
                  email.
                </p>
              )}
            </div>

            <label style={{ display: 'block', marginBottom: 20 }}>
              <div
                style={{
                  ...cap,
                  color: emailError ? '#c0392b' : '#888',
                  fontSize: '0.63rem',
                  marginBottom: 6,
                  transition: 'color 0.15s',
                }}
              >
                Email for receipt
                {emailError ? ' — required' : ' (required for card payment)'}
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (e.target.value) setEmailError(false);
                }}
                placeholder="your@email.com"
                style={{
                  width: '100%',
                  border: 'none',
                  borderBottom: emailError
                    ? '1px solid #c0392b'
                    : '1px solid #ddd8cc',
                  background: 'transparent',
                  padding: '8px 0',
                  fontFamily: "'Raleway', sans-serif",
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.15s',
                }}
              />
            </label>

            {/* ── Shipping address — shown when "ship" selected ────────── */}
            {fulfillment === 'ship' && (
              <div style={{ marginBottom: 20 }}>
                <div
                  style={{
                    ...cap,
                    color: '#888',
                    fontSize: '0.63rem',
                    marginBottom: 10,
                  }}
                >
                  Shipping Address
                </div>
                {[
                  {
                    key: 'line1',
                    placeholder: 'Street address',
                    required: true,
                  },
                  {
                    key: 'line2',
                    placeholder: 'Apt, suite, etc. (optional)',
                    required: false,
                  },
                ].map(({ key, placeholder }) => (
                  <input
                    key={key}
                    type="text"
                    value={shipAddr[key]}
                    onChange={(e) =>
                      setShipAddr((a) => ({ ...a, [key]: e.target.value }))
                    }
                    placeholder={placeholder}
                    style={{
                      display: 'block',
                      width: '100%',
                      border: 'none',
                      borderBottom: '1px solid #ddd8cc',
                      background: 'transparent',
                      padding: '8px 0',
                      marginBottom: 10,
                      fontFamily: "'Raleway', sans-serif",
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                ))}
                <div style={{ display: 'flex', gap: 12 }}>
                  <input
                    type="text"
                    value={shipAddr.city}
                    onChange={(e) =>
                      setShipAddr((a) => ({ ...a, city: e.target.value }))
                    }
                    placeholder="City"
                    style={{
                      flex: 2,
                      border: 'none',
                      borderBottom: '1px solid #ddd8cc',
                      background: 'transparent',
                      padding: '8px 0',
                      fontFamily: "'Raleway', sans-serif",
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      outline: 'none',
                    }}
                  />
                  <input
                    type="text"
                    value={shipAddr.state}
                    onChange={(e) =>
                      setShipAddr((a) => ({ ...a, state: e.target.value }))
                    }
                    placeholder="State"
                    maxLength={2}
                    style={{
                      flex: 1,
                      border: 'none',
                      borderBottom: '1px solid #ddd8cc',
                      background: 'transparent',
                      padding: '8px 0',
                      fontFamily: "'Raleway', sans-serif",
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      outline: 'none',
                      textTransform: 'uppercase',
                    }}
                  />
                  <input
                    type="text"
                    value={shipAddr.zip}
                    onChange={(e) =>
                      setShipAddr((a) => ({ ...a, zip: e.target.value }))
                    }
                    placeholder="ZIP"
                    maxLength={10}
                    style={{
                      flex: 1,
                      border: 'none',
                      borderBottom: '1px solid #ddd8cc',
                      background: 'transparent',
                      padding: '8px 0',
                      fontFamily: "'Raleway', sans-serif",
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      outline: 'none',
                    }}
                  />
                </div>
              </div>
            )}

            {(applePayReady || googlePayReady) && (
              <>
                <p
                  style={{
                    ...cap,
                    color: '#9C7A3C',
                    fontSize: '0.6rem',
                    marginBottom: 8,
                    textAlign: 'center',
                  }}
                >
                  — or pay instantly —
                </p>
                {applePayReady && (
                  <button
                    onClick={handleApplePay}
                    disabled={processing}
                    aria-label="Pay with Apple Pay"
                    style={{
                      WebkitAppearance: '-apple-pay-button',
                      ApplePayButtonType: 'buy',
                      ApplePayButtonStyle: 'black',
                      width: '100%',
                      height: 44,
                      marginBottom: 8,
                      cursor: processing ? 'not-allowed' : 'pointer',
                      opacity: processing ? 0.6 : 1,
                      border: 'none',
                      borderRadius: 4,
                    }}
                  />
                )}
                {/* Google Pay button — rendered + managed by Square SDK */}
                <div
                  id="google-pay-button"
                  style={{
                    width: '100%',
                    height: googlePayReady ? 44 : 0,
                    marginBottom: googlePayReady ? 8 : 0,
                    overflow: 'hidden',
                    opacity: processing ? 0.6 : 1,
                    pointerEvents: processing ? 'none' : 'auto',
                  }}
                />
              </>
            )}

            {sdkReady ? (
              <>
                <div
                  style={{
                    ...cap,
                    color: '#888',
                    fontSize: '0.63rem',
                    marginBottom: 8,
                  }}
                >
                  Card Details
                </div>
                <div id="square-card-container" style={{ marginBottom: 20 }} />
              </>
            ) : (
              <div
                style={{
                  padding: '20px',
                  background: '#ede8da',
                  marginBottom: 20,
                  fontSize: '0.82rem',
                  color: '#888',
                  fontStyle: 'italic',
                }}
              >
                {APP_ID && LOCATION_ID
                  ? 'Loading payment form…'
                  : 'Payment not configured — Square env vars required.'}
              </div>
            )}

            {payError && (
              <div
                style={{
                  padding: '12px 16px',
                  background: '#fdf0f0',
                  border: '1px solid #e8c8c8',
                  marginBottom: 16,
                  fontSize: '0.83rem',
                  color: '#c0392b',
                }}
              >
                {payError}
              </div>
            )}

            <button
              className="book-btn"
              onClick={handleCardPay}
              disabled={
                processing ||
                !sdkReady ||
                (fulfillment === 'ship' &&
                  (!shipAddr.line1 ||
                    !shipAddr.city ||
                    !shipAddr.state ||
                    !shipAddr.zip))
              }
              style={{
                opacity:
                  processing ||
                  !sdkReady ||
                  (fulfillment === 'ship' &&
                    (!shipAddr.line1 ||
                      !shipAddr.city ||
                      !shipAddr.state ||
                      !shipAddr.zip))
                    ? 0.6
                    : 1,
                cursor: processing ? 'not-allowed' : 'pointer',
              }}
            >
              {processing
                ? 'Processing…'
                : `Pay $${(item.price * qty).toFixed(2)}`}
            </button>
            <p
              style={{
                marginTop: 14,
                fontSize: '0.72rem',
                color: '#bbb',
                textAlign: 'center',
                fontStyle: 'italic',
              }}
            >
              Secured by Square &nbsp;·&nbsp; SSL encrypted
            </p>
          </div>
        </div>
      </div>
    );
  }

  /* ─────────────────────────────────────────────────────────────────────────
     SUCCESS VIEW
  ───────────────────────────────────────────────────────────────────────── */
  if (view === 'success')
    return (
      <div
        className="fade section-pad"
        style={{ paddingTop: 28, paddingBottom: 48 }}
      >
        <div
          style={{
            ...tile,
            padding: '60px 48px',
            textAlign: 'center',
            maxWidth: 560,
            margin: '0 auto',
          }}
        >
          <div style={{ ...cap, color: '#9C7A3C', marginBottom: 20 }}>
            ◦ ◦ ◦
          </div>
          <h2
            style={{
              fontFamily: "'Titan One', sans-serif",
              fontWeight: 400,
              fontSize: '2rem',
              letterSpacing: '-0.01em',
              color: '#1a1208',
              marginBottom: 16,
            }}
          >
            Thank You
          </h2>
          <p
            style={{
              fontFamily: "'Raleway', sans-serif",
              fontWeight: 700,
              fontSize: 'clamp(1rem, 1.8vw, 1.1rem)',
              color: '#1a1208',
              lineHeight: 1.65,
              marginBottom: 24,
            }}
          >
            Your order for{' '}
            <strong>
              {qty > 1 ? `${qty} × ` : ''}
              {item?.name}
            </strong>{' '}
            has been received. A receipt has been sent to{' '}
            <strong>{usedEmail}</strong>.
          </p>
          {orderResult?.receiptUrl && (
            <a
              href={orderResult.receiptUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                marginBottom: 32,
                ...cap,
                color: '#9C7A3C',
                fontSize: '0.68rem',
                textDecoration: 'underline',
              }}
            >
              View Receipt →
            </a>
          )}
          <p
            style={{
              fontFamily: "'Raleway', sans-serif",
              fontWeight: 700,
              fontSize: '0.82rem',
              color: '#9C7A3C',
              marginBottom: 32,
              lineHeight: 1.7,
            }}
          >
            {orderResult?.fulfillment === 'pickup'
              ? "We'll reach out by email to coordinate local pickup in Dallas, TX."
              : 'Viri will carefully package your piece and ship it from Dallas within 3–5 business days. You\u2019ll receive a shipping confirmation by email.'}
          </p>
          <div
            style={{
              display: 'flex',
              gap: 12,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <button
              className="book-btn"
              style={{ width: 'auto', padding: '14px 28px' }}
              onClick={() => {
                setOrderResult(null);
                loadProducts(); // refresh so stock reflects the just-completed purchase
                setView('browse');
              }}
            >
              Browse More Pieces
            </button>
            <button className="thin-btn" onClick={onReset}>
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );

  return null;
}
